import React, {
  useMemo,
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react';
import {
  Box,
  Text,
  Stack,
  Group,
  Select,
  Table,
  Drawer,
  CloseButton,
  ScrollArea,
  Tooltip,
  LoadingOverlay,
  Button,
  ActionIcon,
  Card,
  Switch,
  Code,
  Divider,
  Loader,
  Accordion,
  Collapse,
  RingProgress,
  Highlight,
  TextInput,
} from '@mantine/core';
import {
  IconCheck,
  IconDisc,
  IconPlus,
  IconSettings,
  IconTrash,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconBan,
  IconCornerDownRightDouble,
  IconCalculator,
  IconKey,
  IconArrowRight,
} from '@tabler/icons';
import { SourceIcon } from '../utils/SourceIcon';
import { showNotification } from '@mantine/notifications';
import { BagelObjectConf, ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { useIntegrationFields } from '../api/hooks/useIntegrationFields';
import { useDrawerIntegrationObject } from '../../../queries/use-admin-integration-by-key';
import { useAuth0 } from '@auth0/auth0-react';
import { ProcessedFieldSettingsBox } from './ProcessedFieldSettingsBox';
import { TemplateFieldSettingsBox } from '../common/TemplateFieldSettingsBox';
import { isSavedFieldKey, getSavedFieldDescription, SAVED_FIELD_KEYS } from './savedFieldKeys';

// Business category icons
import {
  CustomerCallIcon,
  GeneralIcon,
  HealthNotesIcon,
  LostHoldReasonIcon,
  PmFeedbackIcon,
  RequestBusinessnameIcon,
  SupportCaseTicketIcon,
  SurveyIcon,
} from '../utils/icons/x-symbol-icons';
import { useFields } from '../hooks/useFields';
import { resolveOriginObjectKey } from '../utils/external-object-key';
import { useParams } from 'react-router-dom';
import { useIntegrationObjectsMeta } from '../../../queries/use-integration-objects-meta';
import { LinkedIdeasFilterControls } from './LinkedIdeasFilterControls';

interface BusinessCategory {
  businessName: string | null;
  businessCategory: string | null;
  icon: string;
}

interface NewMapping {
  externalField: string;
  bagelField: string;
  isStatic?: boolean;
  staticValue?: string;
}

interface FieldMappingDrawerProps {
  opened: boolean;
  onClose: () => void;
  selectedObject: ExternalObjectConf | null;
  selectedBagelObject: BagelObjectConf | null;
  integrationKey: string;
  integrationName: string;
  /** All configured objects (with meta fieldsMeta injected) — the single admin object list. */
  externalObjects: ExternalObjectConf[];
  updateExternalObject: (
    originObject: string,
    updates: Partial<ExternalObjectConf>,
  ) => Promise<boolean>;
}

// Helper component for business category icons
const BusinessCategoryIcon: React.FC<{ iconName: string }> = ({ iconName }) => {
  const icons = [
    'bagel_lost_hold_reason',
    'bagel_Support_Case_Ticket',
    'bagel_Customer_Call',
    'bagel_survey',
    'bagel_Health_Notes',
    'bagel_pm_feedback',
    'bagel_request',
    'bagel_opportunity',
    'bagel_general',
    'bagel_interview',
  ];

  if (!iconName || !icons.includes(iconName)) {
    return <GeneralIcon />;
  }

  switch (iconName) {
    case 'bagel_lost_hold_reason':
      return <LostHoldReasonIcon />;
    case 'bagel_Support_Case_Ticket':
      return <SupportCaseTicketIcon />;
    case 'bagel_Customer_Call':
      return <CustomerCallIcon />;
    case 'bagel_survey':
      return <SurveyIcon />;
    case 'bagel_Health_Notes':
      return <HealthNotesIcon />;
    case 'bagel_pm_feedback':
      return <PmFeedbackIcon />;
    case 'bagel_request':
      return <RequestBusinessnameIcon />;
    case 'bagel_opportunity':
      return <RequestBusinessnameIcon />;
    case 'bagel_general':
      return <GeneralIcon />;
    default:
      return <GeneralIcon />;
  }
};

// Highlight text that renders on a single line, truncating with an ellipsis when
// it does not fit the available width and revealing the full value in a tooltip.
// Keeps the search-match highlight while staying resilient to narrow dropdowns.
interface TruncatedHighlightProps extends React.ComponentPropsWithoutRef<typeof Highlight> {
  highlight: string;
}

const TruncatedHighlight = ({ highlight, children, sx, ...props }: TruncatedHighlightProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOverflown, setIsOverflown] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      setIsOverflown(element.scrollWidth > element.clientWidth);
    }
  }, [children]);

  return (
    <Tooltip
      label={children}
      disabled={!isOverflown}
      withinPortal
      multiline
      maw="50vw"
      sx={{ whiteSpace: 'break-spaces' }}
    >
      <Highlight
        ref={ref}
        highlight={highlight || ''}
        truncate
        sx={{ display: 'block', ...sx }}
        {...props}
      >
        {children}
      </Highlight>
    </Tooltip>
  );
};

// SelectItem component with proper ref forwarding
interface BusinessCategoryItemProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label: string;
  icon: string;
  group?: string;
  query?: string;
}

const BusinessCategorySelectItem = forwardRef<HTMLDivElement, BusinessCategoryItemProps>(
  ({ value, label, icon, query, ...others }: BusinessCategoryItemProps, ref) => {
    return (
      <div ref={ref} {...others}>
        <Group noWrap>
          <Box w={20} h={20}>
            {icon && <BusinessCategoryIcon iconName={icon} />}
          </Box>
          <Highlight highlight={query || ''} size="sm">
            {label}
          </Highlight>
        </Group>
      </div>
    );
  },
);

BusinessCategorySelectItem.displayName = 'BusinessCategorySelectItem';

// External field select item with value displayed below label and metadata
interface ExternalFieldItemProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label: string;
  fillPercentage?: number;
  hasStatistics?: boolean;
  totalRecords?: number;
  nonNullCount?: number;
  types?: string[];
  required?: boolean;
  length?: number;
  query?: string;
}

const ExternalFieldSelectItem = forwardRef<HTMLDivElement, ExternalFieldItemProps>(
  (
    {
      value,
      label,
      fillPercentage,
      hasStatistics,
      totalRecords,
      nonNullCount,
      types,
      required,
      length,
      query,
      ...others
    }: ExternalFieldItemProps,
    ref,
  ) => {
    // Prepare tooltip content
    const tooltipContent = useMemo(() => {
      const parts = [];
      if (types && types.length > 0) {
        parts.push(`Types: ${types.join(', ')}`);
      }
      if (
        totalRecords !== undefined &&
        totalRecords !== null &&
        nonNullCount !== undefined &&
        nonNullCount !== null
      ) {
        parts.push(`Records: ${nonNullCount.toLocaleString()}/${totalRecords.toLocaleString()}`);
      }
      if (required) {
        parts.push('Required field');
      }
      if (length !== undefined && length !== null) {
        parts.push(`Max length: ${length}`);
      }
      return parts.length > 0 ? parts.join(' | ') : null;
    }, [types, totalRecords, nonNullCount, required, length]);

    const content = (
      <div ref={ref} {...others}>
        <Group position="apart" align="center" noWrap sx={{ width: '100%' }}>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <TruncatedHighlight highlight={query || ''} size="sm">
              {label}
            </TruncatedHighlight>
            {value !== label && (
              <TruncatedHighlight highlight={query || ''} size="xs" color="dimmed">
                {value}
              </TruncatedHighlight>
            )}
          </Stack>
          {fillPercentage !== undefined && fillPercentage !== null && (
            <RingProgress
              sx={{ flexShrink: 0 }}
              size={44}
              thickness={3}
              sections={[
                {
                  value: fillPercentage,
                  color: fillPercentage > 80 ? 'green' : fillPercentage > 50 ? 'yellow' : 'red',
                },
              ]}
              label={
                <Text
                  size={8}
                  align="center"
                  weight={400}
                  color={!fillPercentage ? 'dimmed' : undefined}
                >
                  {fillPercentage ? `${Math.round(fillPercentage)}%` : 'N/A'}
                </Text>
              }
            />
          )}
        </Group>
      </div>
    );

    return content;
  },
);

ExternalFieldSelectItem.displayName = 'ExternalFieldSelectItem';

// Bagel field select item with key icon for saved fields
interface BagelFieldItemProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label: string;
  query?: string;
}

const BagelFieldSelectItem = forwardRef<HTMLDivElement, BagelFieldItemProps>(
  ({ value, label, query, ...others }: BagelFieldItemProps, ref) => {
    const isSaved = isSavedFieldKey(value);

    return (
      <div ref={ref} {...others}>
        <Group position="apart" align="center" noWrap sx={{ width: '100%' }}>
          <Group spacing={8} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {isSaved && <IconKey size={16} style={{ color: '#5c5ceb', flexShrink: 0 }} />}
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <TruncatedHighlight highlight={query || ''} size="sm">
                {label}
              </TruncatedHighlight>
              {value !== label && (
                <TruncatedHighlight highlight={query || ''} size="xs" color="dimmed">
                  {value}
                </TruncatedHighlight>
              )}
            </Stack>
          </Group>
        </Group>
      </div>
    );
  },
);

BagelFieldSelectItem.displayName = 'BagelFieldSelectItem';

// Custom value component for selected display with key icon
const BagelFieldValue = ({ value, label, ...others }: { value: string; label: string }) => {
  const isSaved = isSavedFieldKey(value);
  const displayLabel = label || value;

  return (
    <Group spacing={6} noWrap {...others}>
      {isSaved && <IconKey size={14} style={{ color: '#5c5ceb', flexShrink: 0 }} />}
      <Text size="sm" truncate>
        {displayLabel}
      </Text>
    </Group>
  );
};

// Utility to convert a string to camelCase
function toCamelCase(str: string) {
  return str
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .replace(/(?:^|\s|_)([a-z])/g, (match, p1, offset) =>
      offset === 0 ? p1.toLowerCase() : p1.toUpperCase(),
    )
    .replace(/\s+/g, '');
}

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Optimized filter function
const createOptimizedFilter = (searchQuery: string) => {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return () => true;

  return (value: string, item: any) => {
    const itemLabel = (item.label || '').toLowerCase();
    const itemValue = (item.value || '').toLowerCase();
    return itemLabel.includes(query) || itemValue.includes(query);
  };
};

// FieldSettingsBox component for field-specific settings
interface FieldSettingsBoxProps {
  field: any;
  updateField: (args: any) => Promise<any>;
  integrationKey: string;
  blacklistedFields: string[];
  onToggleBlacklist: (fieldKey: string, add: boolean) => void;
  isBlacklisting: boolean;
  onStaticToggle?: (field: any, isStatic: boolean, staticValue?: string) => void;
  onDeleteField?: (field: any) => void;
  isDeleting?: boolean;
}

const FieldSettingsBox: React.FC<FieldSettingsBoxProps> = ({
  field,
  updateField,
  integrationKey,
  blacklistedFields,
  onToggleBlacklist,
  isBlacklisting,
  onStaticToggle,
  onDeleteField,
  isDeleting = false,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [staticLoading, setStaticLoading] = React.useState(false);
  const [typeLoading, setTypeLoading] = React.useState(false);
  const [popupLoading, setPopupLoading] = React.useState(false);
  const [checked, setChecked] = React.useState(!!field.redactPII);
  const [isStatic, setIsStatic] = React.useState(field.type === 'static');
  const [showInPopup, setShowInPopup] = React.useState(!!field.showInPopup);
  const [fieldType, setFieldType] = React.useState(() => {
    // Get current field type from originalType (for static fields) or type, default to 'text'
    if (field.type === 'static' && field.originalType) {
      return field.originalType;
    }
    return field.type === 'static' ? 'text' : field.type || 'text';
  });

  React.useEffect(() => {
    setChecked(!!field.redactPII);
    setIsStatic(field.type === 'static');
    setShowInPopup(!!field.showInPopup);

    // Update field type state when field changes
    if (field.type === 'static' && field.originalType) {
      setFieldType(field.originalType);
    } else if (field.type !== 'static') {
      setFieldType(field.type || 'text');
    }
  }, [field.redactPII, field.type, field.originalType, field.showInPopup]);

  const isBlacklisted = blacklistedFields.includes(field.originName || field.name);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.checked;
    setLoading(true);
    setChecked(newValue);
    try {
      await updateField({
        fieldName: field.key || field.fieldPath,
        field: {
          ...field,
          fieldType: 'imported',
          redactPII: newValue,
          origin: integrationKey,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBlacklistToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleBlacklistToggle', field);
    const add = event.currentTarget.checked;
    onToggleBlacklist(field.originName || field.name, add);
  };

  const handleStaticToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsStatic = event.currentTarget.checked;
    setStaticLoading(true);
    setIsStatic(newIsStatic);

    try {
      if (onStaticToggle) {
        await onStaticToggle(field, newIsStatic, newIsStatic ? field.value || '' : undefined);
      } else {
        // Fallback to direct update if no callback provided
        await updateField({
          fieldName: field.key || field.fieldPath,
          field: {
            ...field,
            fieldType: 'imported',
            type: newIsStatic ? 'static' : field.originalType || 'string',
            value: newIsStatic ? field.value || '' : undefined,
            origin: integrationKey,
          },
        });
      }
    } finally {
      setStaticLoading(false);
    }
  };

  const handleDeleteField = () => {
    if (onDeleteField) {
      onDeleteField(field);
    }
  };

  const handleShowInPopupToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.checked;
    setPopupLoading(true);
    setShowInPopup(newValue);
    try {
      await updateField({
        fieldName: field.key || field.fieldPath,
        field: {
          ...field,
          fieldType: 'imported',
          showInPopup: newValue,
          origin: integrationKey,
        },
      });
    } finally {
      setPopupLoading(false);
    }
  };

  const handleFieldTypeChange = async (newType: string | null) => {
    if (!newType || newType === fieldType) return;

    setTypeLoading(true);
    setFieldType(newType);

    try {
      const updatedField = {
        ...field,
        origin: integrationKey,
      };

      if (field.type === 'static') {
        // For static fields, update originalType
        updatedField.originalType = newType;
        updatedField.fieldType = 'static';
      } else {
        // For regular fields, update type
        updatedField.type = newType;
        updatedField.fieldType = 'imported';
      }

      await updateField({
        fieldName: field.key || field.fieldPath,
        field: updatedField,
      });

      showNotification({
        title: 'Field type updated',
        message: `Field type changed to ${newType}`,
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating field type:', error);
      showNotification({
        title: 'Error updating field type',
        message: 'Failed to update field type',
        color: 'red',
      });
      // Revert field type on error
      setFieldType(
        field.type === 'static' && field.originalType ? field.originalType : field.type || 'text',
      );
    } finally {
      setTypeLoading(false);
    }
  };

  // Check if this is a processed field
  const isProcessedField = field.fieldType === 'processed';

  return (
    <Stack spacing="xs">
      {isProcessedField ? null : (
        <>
          <Select
            label="Field Type"
            description="Select the data type for this field"
            value={fieldType}
            onChange={handleFieldTypeChange}
            disabled={typeLoading}
            data={[
              { value: 'text', label: 'Text' },
              { value: 'number', label: 'Number' },
              { value: 'date', label: 'Date' },
            ]}
            size="xs"
          />
          <Switch
            label="Static Field"
            description="Make this field static with a fixed value"
            checked={isStatic}
            onChange={handleStaticToggle}
            disabled={staticLoading}
            color="blue"
          />
          <Switch
            label="Redact PII"
            checked={checked}
            onChange={handleToggle}
            disabled={loading || isStatic}
          />
          <Switch
            label="Show in original item popup"
            checked={showInPopup}
            onChange={handleShowInPopupToggle}
            disabled={popupLoading || isStatic}
          />
          <Switch
            label="Blacklist this field"
            checked={isBlacklisted}
            onChange={handleBlacklistToggle}
            disabled={isBlacklisting}
            color="red"
          />
          {onDeleteField && (
            <Button
              variant="outline"
              color="red"
              size="xs"
              leftIcon={<IconTrash size={14} />}
              onClick={handleDeleteField}
              disabled={isDeleting}
              loading={isDeleting}
            >
              Delete Field
            </Button>
          )}
        </>
      )}
    </Stack>
  );
};

export const FieldMappingDrawer: React.FC<FieldMappingDrawerProps> = ({
  opened,
  onClose,
  selectedObject,
  selectedBagelObject,
  integrationKey,
  integrationName,
  externalObjects,
  updateExternalObject,
}) => {
  if (!integrationKey || !selectedObject || !selectedBagelObject) return null;

  const auth0 = useAuth0();
  // Default Field Mapping source: the catalog object's default `.fields` from
  // GET /admin/integration-by-key (drop-in twin of /integrations/v2/:key). Non-blocking — falls
  // back to the existing list lookup on 404/error/loading.
  const { defaultObject: catalogDefaultObject } = useDrawerIntegrationObject({
    integrationKey,
    originObjectKey: selectedObject.originObjectKey || selectedObject.originObject || '',
    enabled: opened,
  });
  const [newMapping, setNewMapping] = useState<NewMapping | null>(null);
  const [showNewMappingRow, setShowNewMappingRow] = useState(false);
  const [newMappingType, setNewMappingType] = useState<
    'imported' | 'static' | 'processed' | 'template'
  >('imported');
  const [staticFieldValues, setStaticFieldValues] = useState<Record<string, string>>({});
  const [fieldUpdating, setFieldUpdating] = useState<string | null>(null);
  const [extraParamsUpdating, setExtraParamsUpdating] = useState<string | null>(null);
  const { accountId = '' } = useParams<{ accountId: string }>();
  const integrationObjectsMeta = useIntegrationObjectsMeta(accountId, integrationKey);
  const issueTypeNames =
    integrationObjectsMeta.data?.items.find(
      (item) => item.originObjectKey === resolveOriginObjectKey(selectedObject),
    )?.extraParams?.issueTypeNames ?? [];
  const [selectedBusinessName, setSelectedBusinessName] = useState<string | null>(
    selectedObject?.extraParams?.businessName || null,
  );
  const [selectKey, setSelectKey] = useState(Date.now());
  const [externalFieldSearch, setExternalFieldSearch] = useState('');
  const [bagelFieldSearch, setBagelFieldSearch] = useState('');
  const [customMappingSearches, setCustomMappingSearches] = useState<Record<string, string>>({});
  // Enrich match filter state — simple list of {bagelField, valueSource, value} conditions
  const parseEnrichRules = (
    filter: any,
  ): Array<{ bagelField: string; valueSource: 'field' | 'value'; value: string }> => {
    if (!filter?.rules?.length) return [{ bagelField: '', valueSource: 'field', value: '' }];
    return filter.rules
      .filter((r: any) => !r.rules) // skip nested groups
      .map((r: any) => ({
        bagelField: r.field || '',
        valueSource: r.valueSource === 'value' ? ('value' as const) : ('field' as const),
        value: r.value || '',
      }));
  };

  const [enrichMatchRows, setEnrichMatchRows] = useState(
    parseEnrichRules(selectedObject?.extraParams?.enrichMatchFilter),
  );
  const [enrichFilterDirty, setEnrichFilterDirty] = useState(false);

  // Reset enrich filter state when selectedObject changes or enrichOnly is toggled
  useEffect(() => {
    setEnrichMatchRows(parseEnrichRules(selectedObject?.extraParams?.enrichMatchFilter));
    setEnrichFilterDirty(false);
  }, [selectedObject?.originObject, selectedObject?.extraParams?.enrichOnly]);

  // Debounced search values
  const debouncedExternalFieldSearch = useDebounce(externalFieldSearch, 300);
  const debouncedBagelFieldSearch = useDebounce(bagelFieldSearch, 300);
  const debouncedCustomMappingSearches = useDebounce(customMappingSearches, 300);

  // Memoized filter functions for performance
  const externalFieldFilter = useMemo(
    () => createOptimizedFilter(debouncedExternalFieldSearch),
    [debouncedExternalFieldSearch],
  );

  const bagelFieldFilter = useMemo(
    () => createOptimizedFilter(debouncedBagelFieldSearch),
    [debouncedBagelFieldSearch],
  );

  const getCustomMappingFilter = useCallback(
    (fieldKey: string) => {
      return createOptimizedFilter(debouncedCustomMappingSearches[fieldKey] || '');
    },
    [debouncedCustomMappingSearches],
  );

  // Update selected business name when selectedObject changes
  React.useEffect(() => {
    if (selectedObject?.extraParams?.businessName) {
      setSelectedBusinessName(selectedObject.extraParams.businessName);

      // Force select to reinitialize with a slight delay
      setTimeout(() => {
        setSelectKey(Date.now());
      }, 50);
    } else {
      setSelectedBusinessName(null);
    }
  }, [selectedObject]);

  const {
    fields: object2Fields,
    blacklistedFields,
    isLoading: isFieldsLoading,
    updateField,
    updateFieldAsync,
    addOrRemoveBlacklistedFieldAsync,
    isBlacklisting,
    deleteFieldAsync,
    isDeleting,
    validateField,
    isValidating,
  } = useIntegrationFields(
    integrationKey,
    selectedBagelObject.key,
    selectedObject.originObject || '',
    auth0,
  );

  console.log('object2Fields', {
    selectedBagelObject: selectedBagelObject.key,
    originObject: selectedObject?.originObject || '',
    object2Fields,
  });

  // Get available fields for mapping
  const initialBagelFields = useMemo(() => {
    return selectedBagelObject.fields
      .filter((field: any) => field && field.fieldPath && field.name)
      .map((field: any) => ({
        value: String(field.fieldPath || ''),
        label: String(field.name || field.fieldPath || ''),
        type: field.type,
        ...field,
      }));
  }, [selectedBagelObject]);

  // Add state for manageable bagel fields
  const [availableBagelFields, setAvailableBagelFields] = useState<Array<any>>(initialBagelFields);

  // Update availableBagelFields when initialBagelFields changes
  React.useEffect(() => {
    setAvailableBagelFields(initialBagelFields);
  }, [initialBagelFields]);

  // Get initial external fields for mapping with enhanced metadata
  const initialExternalFields = useMemo(() => {
    return (selectedObject.fieldsMeta || [])
      .filter(
        (field) =>
          field &&
          (field.key || (field as any).value) &&
          (field.label || field.key || (field as any).value),
      )
      .map((field) => ({
        ...field,
        value: String(field.key || (field as any).value || ''),
        label: String(field.label || field.key || (field as any).value || ''),
        fillPercentage: (field as any).fillPercentage,
        hasStatistics: (field as any).hasStatistics,
        totalRecords: (field as any).totalRecords,
        nonNullCount: (field as any).nonNullCount,
        types: (field as any).types,
        required: (field as any).required,
        length: (field as any).length,
      }));
  }, [selectedObject]);

  // Add state for manageable external fields
  const [availableExternalFields, setAvailableExternalFields] =
    useState<Array<any>>(initialExternalFields);

  // Update availableExternalFields when initialExternalFields changes
  React.useEffect(() => {
    setAvailableExternalFields(initialExternalFields);
  }, [initialExternalFields]);

  // Get fields with disabled state for custom mapping
  const getFieldsWithDisabledState = (currentField: string) => {
    return availableBagelFields.map((field) => ({
      ...field,
      disabled: (object2Fields || []).some((f: any) => {
        // Disable if this Bagel field is mapped to another external field (not the current one)
        return f.fieldKey !== currentField && f.fieldPath === field.value;
      }),
      isSavedField: isSavedFieldKey(field.value),
      savedFieldDescription: getSavedFieldDescription(field.value),
    }));
  };

  // Handle field mapping change for custom mappings
  const handleFieldMappingChange = async (originField: string, key: string) => {
    console.log('handleFieldMappingChange', { selectedBagelObject, originField, key });
    // First try to find the field in selectedBagelObject.fields
    let field = selectedBagelObject.fields.find((f: any) => f.fieldPath === key);

    // If not found in original fields, look in our availableBagelFields state
    // which includes newly created fields
    if (!field) {
      const newField = availableBagelFields.find((f) => f.value === key);
      if (newField) {
        // Create a compatible field object from our new field
        field = {
          ...newField,
          name: newField.label,
          fieldPath: newField.value,
          type: newField.type || 'string',
          fieldType: 'imported',
        };
      } else {
        console.error('Field not found:', key);
        return;
      }
    }

    console.log('handleFieldMappingChange field found:', field);

    try {
      setFieldUpdating(originField);
      await updateField({
        fieldName: key,
        field: {
          name: field?.name,
          fieldPath: field?.fieldPath,
          key: field?.fieldPath,
          fieldType: 'imported',
          type: field?.type,
          origin: integrationKey,
          originName: originField,
        },
      });
      showNotification({
        title: `Field updated: ${originField}`,
        message: 'Field updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error:', error);
      showNotification({
        title: `Error updating field: ${originField}`,
        message: 'Error updating field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle extra mapping (idea/evidence) change
  const handleFieldMappingExtraChange = async (
    originField: string,
    fieldKey: string,
    extraKey: string,
    value: string,
  ) => {
    // Find the full field object as in handleFieldMappingChange
    console.log('handleFieldMappingExtraChange', {
      selectedBagelObject,
      originField,
      fieldKey,
      extraKey,
      value,
    });
    const field = object2Fields.find((f: any) => f.fieldPath === fieldKey);

    if (!field) {
      console.error('Field not found:', fieldKey);
      return;
    }
    try {
      setFieldUpdating(originField);
      await updateField({
        fieldName: fieldKey,
        field: {
          ...field,
          fieldType: 'imported',
          [extraKey]: value,
        },
      });
    } catch (error) {
      console.error('Error updating extra mapping:', error);
      showNotification({
        title: `Error updating field: ${originField}`,
        message: 'Error updating field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle adding new mapping
  const handleAddNewMapping = () => {
    if (newMappingType === 'static') {
      // Handle static field creation
      if (!newMapping?.externalField || !newMapping.staticValue) return;
      handleAddNewStaticField(newMapping.externalField, newMapping.staticValue);
    } else if (newMappingType === 'processed') {
      // Handle processed field creation
      if (!newMapping?.externalField) return;
      handleAddNewProcessedField(newMapping.externalField);
    } else if (newMappingType === 'template') {
      // Handle template field creation
      if (!newMapping?.externalField) return;
      handleAddNewTemplateField(newMapping.externalField);
    } else {
      // Handle regular field mapping (imported)
      if (!newMapping?.externalField || !newMapping.bagelField) return;
      handleFieldMappingChange(newMapping.externalField, newMapping.bagelField);
      setNewMapping(null);
      setShowNewMappingRow(false);
    }
  };

  // Handle adding new static field
  const handleAddNewStaticField = async (externalField: string, staticValue: string) => {
    if (!externalField || !staticValue) return;

    try {
      setFieldUpdating(externalField);
      await updateField({
        fieldName: externalField,
        field: {
          name: externalField,
          key: externalField,
          fieldType: 'static' as any, // Use 'static' as fieldType
          value: staticValue,
          origin: integrationKey,
        },
      });
      showNotification({
        title: `Static field added: ${externalField}`,
        message: 'Static field created successfully',
        color: 'green',
      });
      setNewMapping(null);
      setShowNewMappingRow(false);
      setNewMappingType('imported');
    } catch (error) {
      console.error('Error adding static field:', error);
      showNotification({
        title: `Error adding static field: ${externalField}`,
        message: 'Error creating static field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle adding new processed field
  const handleAddNewProcessedField = async (fieldName: string) => {
    if (!fieldName) return;

    try {
      setFieldUpdating(fieldName);
      // Create a default processed field with empty pipeline
      await updateField({
        fieldName: fieldName,
        field: {
          name: fieldName,
          key: fieldName,
          fieldType: 'processed',
          type: 'text',
          origin: integrationKey,
          pipeline: {
            operation: 'castOnly',
            field: '',
            cast: 'string',
          },
        },
      });
      showNotification({
        title: `Processed field added: ${fieldName}`,
        message: 'Processed field created successfully. Click the 3-dot menu to edit the pipeline.',
        color: 'green',
      });
      setNewMapping(null);
      setShowNewMappingRow(false);
      setNewMappingType('imported');
    } catch (error) {
      console.error('Error adding processed field:', error);
      showNotification({
        title: `Error adding processed field: ${fieldName}`,
        message: 'Error creating processed field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle adding new template field
  const handleAddNewTemplateField = async (fieldName: string) => {
    if (!fieldName) return;

    try {
      setFieldUpdating(fieldName);
      // Create a default template field with empty template
      await updateField({
        fieldName: fieldName,
        field: {
          name: fieldName,
          key: fieldName,
          fieldType: 'template',
          type: 'text',
          origin: integrationKey,
          template: '',
        },
      });
      showNotification({
        title: `Template field added: ${fieldName}`,
        message:
          'Template field created successfully. Click the 3-dot menu to configure the template.',
        color: 'green',
      });
      setNewMapping(null);
      setShowNewMappingRow(false);
      setNewMappingType('imported');
    } catch (error) {
      console.error('Error adding template field:', error);
      showNotification({
        title: `Error adding template field: ${fieldName}`,
        message: 'Error creating template field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  console.log('object2Fields', object2Fields);
  // Get mapped fields for custom mapping - include regular fields (with originName), static fields (type === 'static'), processed fields (fieldType === 'processed'), and template fields (fieldType === 'template')
  const customMappedFields = useMemo(() => {
    if (!object2Fields) return [];
    return object2Fields
      .filter(
        (field: any) =>
          field.originName ||
          field.type === 'static' ||
          field.fieldType === 'processed' ||
          field.fieldType === 'template',
      )
      .map((field: any) => ({
        fieldPath: field.fieldPath,
        key: field.key,
        originName: field.originName,
        name: field.name,
        type: field.type,
        value: field.value, // Include value for static fields
        fieldKey: field.fieldKey, // Include fieldKey for proper identification
        fieldType: field.fieldType, // Include fieldType to distinguish processed/template fields
        pipeline: field.pipeline, // Include pipeline for processed fields
        template: field.template, // Include template for template fields
        origin: field.origin, // Include origin for processed/template fields
      }));
  }, [object2Fields]);

  // Default-mapping source, in priority order:
  // 1) the catalog object from GET /admin/integration-by-key (available.fields, connected fallback);
  // 2) the existing list lookup (bagelObjectKey + originObject match);
  // 3) whichever list object is the global default schema (e.g. Jira's shared schema object).
  let availableExternalObject =
    catalogDefaultObject && catalogDefaultObject.fields?.length ? catalogDefaultObject : undefined;
  if (!availableExternalObject || !availableExternalObject.fields?.length) {
    availableExternalObject = externalObjects.find(
      (obj: any) =>
        obj.bagelObjectKey?.toLowerCase() == selectedObject.bagelObjectKey?.toLowerCase() &&
        (obj.originObjectKey?.toLowerCase() == selectedObject.originObjectKey?.toLowerCase() ||
          obj.originObject?.toLowerCase() == selectedObject.originObject?.toLowerCase()),
    );
  }
  if (!availableExternalObject || !availableExternalObject.fields?.length) {
    availableExternalObject = externalObjects.find((obj: any) => obj.isGlobalDefault === true);
  }

  // console.log('availableExternalObjects', availableExternalObject, selectedObject);
  const { businessCategoryMapping } = useFields();

  // console.log('businessCategoryMapping', businessCategoryMapping);

  // Helper function to update extraParams
  const updateExtraParams = async (updates: Record<string, any>) => {
    if (!selectedObject) return;

    try {
      const extraParamsUpdates = {
        extraParams: {
          ...(selectedObject.extraParams || {}),
          ...updates,
        },
      } as Partial<ExternalObjectConf>;

      const saved = await updateExternalObject(
        resolveOriginObjectKey(selectedObject),
        extraParamsUpdates,
      );
      if (!saved) throw new Error('Failed to save configuration');
      showNotification({
        title: `Settings updated`,
        message: 'Configuration updated successfully',
        color: 'green',
      });
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      showNotification({
        title: 'Error updating settings',
        message: 'Failed to update configuration',
        color: 'red',
      });
      return false;
    }
  };

  // Helper function to prepare business name select data
  const prepareBusinessNameData = () => {
    // Start with the filtered business categories
    const options = (businessCategoryMapping as BusinessCategory[])
      .filter((item) => item.businessName)
      .map((item) => ({
        value: item.businessName as string,
        label: item.businessName as string,
        group: item.businessCategory || 'Other',
        icon: item.icon,
      }));

    // If current value exists but is not in options, add it
    const currentValue = selectedObject?.extraParams?.businessName;
    if (currentValue && !options.some((opt) => opt.value === currentValue)) {
      console.log('Adding missing option:', currentValue);
      options.push({
        value: currentValue,
        label: currentValue,
        group: 'Custom',
        icon: 'default_icon',
      });
    }

    return options;
  };

  // Add state for tracking expanded state
  const [isFieldsExpanded, setIsFieldsExpanded] = useState(false);

  // Add state for tracking which row's settings are open
  const [openSettingsRow, setOpenSettingsRow] = useState<string | null>(null);

  const handleToggleBlacklist = (fieldKey: string, add: boolean) => {
    addOrRemoveBlacklistedFieldAsync({
      action: add ? 'add' : 'remove',
      field: fieldKey,
    });
  };

  // Handle static field toggle and value changes
  const handleStaticToggle = async (field: any, isStatic: boolean, staticValue?: string) => {
    const fieldId = field.originName || field.key || field.fieldKey;
    try {
      setFieldUpdating(fieldId);

      const updatedField = {
        ...field,
        origin: integrationKey,
      };

      if (isStatic) {
        // Convert to static field
        updatedField.fieldType = 'static' as any; // Use 'static' as fieldType
        updatedField.value = staticValue || '';
        // Store original type for potential conversion back
        if (field.type !== 'static') {
          updatedField.originalType = field.type;
        }
        // Remove mapping-related properties when converting to static
        delete updatedField.fieldPath;
        delete updatedField.originName;
        delete updatedField.type; // Remove type field as requested
      } else {
        // Convert back to regular field
        updatedField.fieldType = 'imported';
        updatedField.type = field.originalType || 'string';
        updatedField.fieldPath = field.key || field.fieldPath;
        updatedField.originName = field.originName;
        // Remove static-specific properties
        delete updatedField.value;
        delete updatedField.originalType;
      }

      await updateField({
        fieldName: field.key || field.fieldPath || field.fieldKey,
        field: updatedField,
      });

      showNotification({
        title: `Field updated: ${fieldId}`,
        message: isStatic ? 'Field converted to static' : 'Field converted to mapped',
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating static field:', error);
      showNotification({
        title: `Error updating field: ${fieldId}`,
        message: 'Error updating field',
        color: 'red',
      });
    } finally {
      setFieldUpdating(null);
    }
  };

  // Handle deleting a field
  const handleDeleteField = async (field: any) => {
    const fieldId = field.originName || field.key || field.fieldKey || field.name;
    try {
      // Prepare the delete request based on field type
      let deleteRequest;
      if (field.type === 'static') {
        deleteRequest = {
          field: {
            key: field.key || field.fieldKey || field.name,
            type: 'static',
            origin: integrationKey,
          },
        };
      } else {
        deleteRequest = {
          field: {
            key: field.key || field.fieldKey || field.name,
            origin: integrationKey,
            originName: field.originName,
          },
        };
      }

      await deleteFieldAsync(deleteRequest);
      showNotification({
        title: `Field deleted: ${fieldId}`,
        message: 'Field deleted successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting field:', error);
      showNotification({
        title: `Error deleting field: ${fieldId}`,
        message: 'Error deleting field',
        color: 'red',
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      padding={0}
      position="right"
      size="lg"
      withCloseButton={false}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isFieldsLoading} overlayBlur={2} />
        <Box>
          <Box
            p="md"
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.colors.gray[3]}`,
              backgroundColor: theme.white,
              position: 'sticky',
              top: 0,
              zIndex: 100,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            })}
          >
            <Group position="apart">
              <SourceIcon sourceName={integrationKey} width={20} height={20} />
              <Text weight={500}>{`Map Fields - ${selectedObject.label}`}</Text>
              <CloseButton onClick={onClose} />
            </Group>
          </Box>

          {/* Extra configurations */}
          <Card withBorder shadow="sm" m="xs" p={0}>
            <Group align="center" spacing={10} py={6} px="xs" bg="#f8f9fa">
              <IconSettings size={16} color="#868e96" />
              <Text size="xs" color="dimmed" weight={500}>
                Extra configurations
              </Text>
            </Group>
            <Divider />
            {/* Create linked Ideas */}
            {selectedBagelObject?.supportsIdeaCreation !== undefined && (
              <Group position="apart" p="xs">
                <Switch
                  label="Create linked Ideas"
                  checked={selectedObject?.extraParams?.createLinkedIdeas || false}
                  onChange={async (event) => {
                    // Guard against null event or currentTarget
                    if (!event || !event.currentTarget || !selectedObject) return;

                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('createLinkedIdeas');
                    await updateExtraParams({ createLinkedIdeas: isChecked }).finally(() => {
                      setExtraParamsUpdating(null);
                    });
                  }}
                />
                {extraParamsUpdating == 'createLinkedIdeas' && <Loader size="xs" />}
              </Group>
            )}
            {integrationKey === 'jira' && selectedObject?.extraParams?.createLinkedIdeas && (
              <LinkedIdeasFilterControls
                key={resolveOriginObjectKey(selectedObject)}
                issueTypeNames={issueTypeNames}
                savedFilter={selectedObject.extraParams.linkedIdeasFilter}
                saving={extraParamsUpdating === 'linkedIdeasFilter'}
                onSave={async (linkedIdeasFilter) => {
                  setExtraParamsUpdating('linkedIdeasFilter');
                  return updateExtraParams({ linkedIdeasFilter }).finally(() => {
                    setExtraParamsUpdating(null);
                  });
                }}
              />
            )}
            {/* Update linked Ideas - only update existing linked ideas, do not create new ones */}
            {selectedBagelObject?.supportsIdeaCreation !== undefined && (
              <Group position="apart" p="xs">
                <Switch
                  label="Update linked Ideas"
                  checked={selectedObject?.extraParams?.updateLinkedIdeas || false}
                  onChange={async (event) => {
                    if (!event || !event.currentTarget || !selectedObject) return;
                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('updateLinkedIdeas');
                    await updateExtraParams({ updateLinkedIdeas: isChecked }).finally(() => {
                      setExtraParamsUpdating(null);
                    });
                  }}
                />
                {extraParamsUpdating === 'updateLinkedIdeas' && <Loader size="xs" />}
              </Group>
            )}
            {/* Create linked Evidence */}
            {selectedBagelObject?.supportsEvidenceCreation !== undefined && (
              <Group position="apart" p="xs">
                <Switch
                  label="Create linked Evidence"
                  checked={selectedObject?.extraParams?.createLinkedEvidence || false}
                  onChange={async (event) => {
                    // Guard against null event or currentTarget
                    if (!event || !event.currentTarget || !selectedObject) return;

                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('createLinkedEvidence');
                    await updateExtraParams({ createLinkedEvidence: isChecked }).finally(() => {
                      setExtraParamsUpdating(null);
                    });
                  }}
                />
                {extraParamsUpdating == 'createLinkedEvidence' && <Loader size="xs" />}
              </Group>
            )}
            {/* Cut items by thread - Slack only */}
            {integrationKey === 'slack' && (
              <Group position="apart" p="xs">
                <Tooltip
                  label="When on, each thread (root message + replies) is synced as a separate item; when off, items are grouped by day."
                  withArrow
                  multiline
                  width={280}
                  withinPortal
                >
                  <div>
                    <Switch
                      label="Cut items by thread"
                      checked={selectedObject?.extraParams?.cutByThread ?? false}
                      onChange={async (event) => {
                        if (!event?.currentTarget || !selectedObject) return;
                        const isChecked = event.currentTarget.checked;
                        setExtraParamsUpdating('cutByThread');
                        await updateExtraParams({ cutByThread: isChecked }).finally(() => {
                          setExtraParamsUpdating(null);
                        });
                      }}
                    />
                  </div>
                </Tooltip>
                {extraParamsUpdating === 'cutByThread' && <Loader size="xs" />}
              </Group>
            )}
            {/* Import all external fields */}
            {(integrationKey === 'salesforce' || integrationKey === 'airtable') && (
              <Group position="apart" p="xs">
                <Switch
                  label="Import all external fields"
                  checked={selectedObject?.extraParams?.importAllFields || false}
                  onChange={async (event) => {
                    // Guard against null event or currentTarget
                    if (!event || !event.currentTarget || !selectedObject) return;

                    const isChecked = event.currentTarget.checked;
                    setExtraParamsUpdating('importAllFields');
                    await updateExtraParams({ importAllFields: isChecked }).finally(() => {
                      setExtraParamsUpdating(null);
                    });
                  }}
                />
                {extraParamsUpdating == 'importAllFields' && <Loader size="xs" />}
              </Group>
            )}
            {/* <Divider variant='dashed' /> */}
            {/* Business Name */}
            {/* <Group p="xs" spacing={0} position="apart">
              <Text size="sm" weight={500}>Business Name</Text>
              <Select
                key={selectKey}
                miw={250}
                data={prepareBusinessNameData()}
                value={selectedBusinessName || ''}
                rightSection={extraParamsUpdating == 'businessName' && <Loader size="xs" />  }
                onChange={async (value) => {
                  // Only trigger update if value actually changed
                  if (value === selectedBusinessName) return;

                  if (!selectedObject) return;

                  // Update local state immediately for better UX
                  setSelectedBusinessName(value);
                  setExtraParamsUpdating('businessName');
                  const success = await updateExtraParams({ businessName: value || '' })
                    .finally(() => {
                      setExtraParamsUpdating(null);
                    });

                  // Revert state on error
                  if (!success) {
                    setSelectedBusinessName(selectedObject?.extraParams?.businessName || null);
                  }
                }}
                placeholder="Select business name"
                // searchable
                clearable
                withinPortal
                nothingFound="No matching business names"
                // onSearchChange={setSearchQuery}
                // searchValue={searchQuery}
                // filter={(value, item) => {
                //   const labelMatch = item.label?.toLowerCase().includes(value.toLowerCase().trim()) || false;
                //   const groupMatch = item.group?.toLowerCase().includes(value.toLowerCase().trim()) || false;
                //   return labelMatch || groupMatch;
                // }}
                itemComponent={BusinessCategorySelectItem}
              />
            </Group> */}
            <Divider variant="dashed" />
            {/* Enrich Only Mode */}
            <Group position="apart" p="xs">
              <Tooltip
                label="When enabled, synced items will only update existing records — no new records will be created"
                withArrow
                multiline
                width={260}
                withinPortal
              >
                <div>
                  <Switch
                    label="Enrich Only"
                    checked={selectedObject?.extraParams?.enrichOnly || false}
                    onChange={async (event) => {
                      if (!event || !event.currentTarget || !selectedObject) return;
                      const isChecked = event.currentTarget.checked;
                      setExtraParamsUpdating('enrichOnly');
                      await updateExtraParams({
                        enrichOnly: isChecked,
                        ...(!isChecked && { enrichMatchFilter: null }),
                      }).finally(() => {
                        setExtraParamsUpdating(null);
                      });
                      if (!isChecked) {
                        setEnrichMatchRows(parseEnrichRules(null));
                        setEnrichFilterDirty(false);
                      }
                    }}
                  />
                </div>
              </Tooltip>
              {extraParamsUpdating === 'enrichOnly' && <Loader size="xs" />}
            </Group>
            {selectedObject?.extraParams?.enrichOnly && (
              <Box p="xs" pt={0}>
                <Text size="xs" weight={500} mb={4}>
                  Match Filter
                </Text>
                <Text size="xs" color="dimmed" mb="xs">
                  All conditions are combined with AND. &quot;Incoming field&quot; reads from the
                  incoming item at runtime; &quot;Static value&quot; uses a fixed string.
                </Text>
                <Stack spacing={6}>
                  {enrichMatchRows.map((row, idx) => (
                    <Group key={idx} spacing={4} noWrap align="flex-end">
                      <Select
                        size="xs"
                        placeholder="Bagel field"
                        searchable
                        creatable
                        withinPortal
                        getCreateLabel={(q) => `+ ${q}`}
                        onCreate={(q) => {
                          const item = { value: q, label: q };
                          const next = [...enrichMatchRows];
                          next[idx] = { ...row, bagelField: q };
                          setEnrichMatchRows(next);
                          setEnrichFilterDirty(true);
                          return item;
                        }}
                        data={(() => {
                          const items = initialBagelFields.map((f: any) => ({
                            value: f.value,
                            label: f.label,
                          }));
                          if (!items.some((i) => i.value === '_id')) {
                            items.unshift({ value: '_id', label: '_id' });
                          }
                          if (row.bagelField && !items.some((i) => i.value === row.bagelField)) {
                            items.push({ value: row.bagelField, label: row.bagelField });
                          }
                          return items;
                        })()}
                        value={row.bagelField}
                        onChange={(val) => {
                          const next = [...enrichMatchRows];
                          next[idx] = { ...row, bagelField: val || '' };
                          setEnrichMatchRows(next);
                          setEnrichFilterDirty(true);
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Text size="xs" color="dimmed" px={2}>
                        =
                      </Text>
                      <Select
                        size="xs"
                        data={[
                          { value: 'field', label: 'Incoming field' },
                          { value: 'value', label: 'Static value' },
                        ]}
                        value={row.valueSource}
                        withinPortal
                        onChange={(val) => {
                          const next = [...enrichMatchRows];
                          next[idx] = {
                            ...row,
                            valueSource: (val as 'field' | 'value') || 'field',
                            value: '',
                          };
                          setEnrichMatchRows(next);
                          setEnrichFilterDirty(true);
                        }}
                        sx={{ width: 130 }}
                      />
                      {row.valueSource === 'field' ? (
                        <Select
                          size="xs"
                          placeholder="External field"
                          searchable
                          creatable
                          withinPortal
                          getCreateLabel={(q) => `+ ${q}`}
                          onCreate={(q) => {
                            const item = { value: q, label: q };
                            const next = [...enrichMatchRows];
                            next[idx] = { ...row, value: q };
                            setEnrichMatchRows(next);
                            setEnrichFilterDirty(true);
                            return item;
                          }}
                          data={(() => {
                            const items = availableExternalFields
                              .filter((f: any) => f && (f.key || f.value) && f.label)
                              .map((f: any) => ({
                                value: String(f.key || f.value || ''),
                                label: String(f.label || f.key || f.value || ''),
                              }));
                            if (row.value && !items.some((i) => i.value === row.value)) {
                              items.push({ value: row.value, label: row.value });
                            }
                            return items;
                          })()}
                          value={row.value}
                          onChange={(val) => {
                            const next = [...enrichMatchRows];
                            next[idx] = { ...row, value: val || '' };
                            setEnrichMatchRows(next);
                            setEnrichFilterDirty(true);
                          }}
                          sx={{ flex: 1 }}
                        />
                      ) : (
                        <TextInput
                          size="xs"
                          placeholder="Static value"
                          value={row.value}
                          onChange={(e) => {
                            const next = [...enrichMatchRows];
                            next[idx] = { ...row, value: e.currentTarget.value };
                            setEnrichMatchRows(next);
                            setEnrichFilterDirty(true);
                          }}
                          sx={{ flex: 1 }}
                        />
                      )}
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        disabled={enrichMatchRows.length <= 1}
                        onClick={() => {
                          setEnrichMatchRows(enrichMatchRows.filter((_, i) => i !== idx));
                          setEnrichFilterDirty(true);
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
                <Group position="apart" mt="xs">
                  <Button
                    size="xs"
                    variant="light"
                    leftIcon={<IconPlus size={14} />}
                    onClick={() => {
                      setEnrichMatchRows([
                        ...enrichMatchRows,
                        { bagelField: '', valueSource: 'field', value: '' },
                      ]);
                      setEnrichFilterDirty(true);
                    }}
                  >
                    Add Condition
                  </Button>
                  <Button
                    size="xs"
                    disabled={!enrichFilterDirty}
                    loading={extraParamsUpdating === 'enrichMatchFilter'}
                    onClick={async () => {
                      const filter = {
                        combinator: 'and' as const,
                        rules: enrichMatchRows
                          .filter((r) => r.bagelField && r.value)
                          .map((r) => ({
                            field: r.bagelField,
                            operator: 'equals' as const,
                            value: r.value,
                            valueSource: r.valueSource,
                          })),
                      };
                      setExtraParamsUpdating('enrichMatchFilter');
                      const success = await updateExtraParams({
                        enrichMatchFilter: filter,
                      }).finally(() => {
                        setExtraParamsUpdating(null);
                      });
                      if (success) {
                        setEnrichFilterDirty(false);
                      }
                    }}
                  >
                    Save Filter
                  </Button>
                </Group>
              </Box>
            )}
          </Card>

          <ScrollArea>
            <Box>
              {/* Field Mapping Table with all fields in one accordion */}
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <thead style={{ borderTop: '1px solid #dee2e6' }}>
                  <tr>
                    <th style={{ width: '40%' }}>{integrationName} Field</th>
                    <th style={{ width: '48%', backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                      Bagel Field
                    </th>
                    <th style={{ width: '8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Default Auto-Mapped Fields in a Collapse */}
                  {availableExternalObject &&
                    (availableExternalObject?.fields?.length || 0) > 0 && (
                      <>
                        <tr
                          style={{
                            backgroundColor: '#5c5ceb0d',
                            cursor: 'pointer',
                            height: '56px', // Standard row height
                          }}
                          onClick={() => setIsFieldsExpanded(!isFieldsExpanded)}
                        >
                          <td colSpan={4}>
                            <Group spacing="xs">
                              {isFieldsExpanded ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                              <Text weight={500}>Default Field Mapping</Text>
                            </Group>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4} style={{ padding: 0 }}>
                            <Collapse in={isFieldsExpanded}>
                              <Table>
                                <tbody>
                                  {availableExternalObject &&
                                    availableExternalObject?.fields?.map(
                                      (field: any, index: number) => {
                                        const rowKey = `default-${index}`;
                                        const isDefaultBlacklisted = blacklistedFields.includes(
                                          field.fieldPath,
                                        );
                                        const externalFieldMeta = selectedObject.fieldsMeta?.find(
                                          (f: any) =>
                                            f.key === field.originName || f.key === field.fieldPath,
                                        );
                                        return (
                                          <React.Fragment key={rowKey}>
                                            <tr style={{ height: '48px' }}>
                                              <td style={{ width: '40%' }}>
                                                <Group spacing={2}>
                                                  {isDefaultBlacklisted && (
                                                    <Tooltip
                                                      label="This field is blacklisted"
                                                      withArrow
                                                    >
                                                      <span
                                                        style={{
                                                          marginRight: 8,
                                                          verticalAlign: 'middle',
                                                          color: 'red',
                                                          display: 'inline-flex',
                                                          alignItems: 'center',
                                                        }}
                                                      >
                                                        <IconBan size={18} />
                                                      </span>
                                                    </Tooltip>
                                                  )}
                                                  <Stack spacing={2}>
                                                    <Text size="sm" weight={500}>
                                                      {externalFieldMeta?.label ||
                                                        field.originName ||
                                                        field.fieldPath ||
                                                        field.name ||
                                                        field.key}
                                                    </Text>
                                                    <Text size="xs" color="dimmed">
                                                      {field.originName ||
                                                        field.fieldPath ||
                                                        field.key}{' '}
                                                      ({field.type || '-'})
                                                    </Text>
                                                  </Stack>
                                                </Group>
                                              </td>
                                              <td
                                                style={{
                                                  width: '48%',
                                                  backgroundColor: 'rgba(92, 92, 235, 0.05)',
                                                }}
                                              >
                                                <Select
                                                  data={[field.key || field.name]}
                                                  value={field.key || field.name}
                                                  disabled
                                                  withinPortal
                                                  itemComponent={({ query, ...props }) => (
                                                    <ExternalFieldSelectItem
                                                      {...props}
                                                      query={query}
                                                    />
                                                  )}
                                                />
                                              </td>
                                              <td
                                                style={{
                                                  width: '8%',
                                                  textAlign: 'center',
                                                  position: 'relative',
                                                }}
                                              >
                                                <Tooltip withArrow label="Configure" withinPortal>
                                                  <ActionIcon
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setOpenSettingsRow(
                                                        openSettingsRow === rowKey ? null : rowKey,
                                                      );
                                                    }}
                                                  >
                                                    <IconDotsVertical size={18} />
                                                  </ActionIcon>
                                                </Tooltip>
                                              </td>
                                            </tr>
                                            {/* Collapsible settings row */}
                                            {openSettingsRow === rowKey && (
                                              <tr>
                                                <td
                                                  colSpan={4}
                                                  style={{ background: '#f8f9fa', padding: 0 }}
                                                >
                                                  <Collapse in={true}>
                                                    <Box
                                                      p="sm"
                                                      sx={{
                                                        boxShadow:
                                                          'inset 0 8px 12px -8px rgba(0,0,0,0.10), inset 0 -8px 12px -8px rgba(0,0,0,0.10)',
                                                      }}
                                                    >
                                                      {field.fieldType === 'processed' ? (
                                                        <ProcessedFieldSettingsBox
                                                          field={field}
                                                          updateField={updateFieldAsync}
                                                          integrationKey={integrationKey}
                                                          object={selectedBagelObject.key}
                                                          originObject={
                                                            selectedObject.originObject || ''
                                                          }
                                                          onDeleteField={handleDeleteField}
                                                          isDeleting={isDeleting}
                                                        />
                                                      ) : field.fieldType === 'template' ? (
                                                        <TemplateFieldSettingsBox
                                                          field={field}
                                                          availableFields={[
                                                            ...(
                                                              availableExternalObject?.fields || []
                                                            ).map((f: any) => {
                                                              // For original fields, use key, path, fieldPath, name, or label as fallback
                                                              const fieldKey =
                                                                f.key ||
                                                                f.path ||
                                                                f.fieldPath ||
                                                                f.name ||
                                                                f.label;
                                                              return {
                                                                key: fieldKey,
                                                                label:
                                                                  f.label || f.name || fieldKey,
                                                                group: 'Original' as const,
                                                                type: f.type || 'text',
                                                              };
                                                            }),
                                                            ...customMappedFields.map((f: any) => ({
                                                              key: f.key || f.fieldPath || f.name,
                                                              label: f.name || f.key || f.fieldPath,
                                                              group: 'Mapped' as const,
                                                              type: f.type || 'text',
                                                            })),
                                                          ]}
                                                          integrationKey={integrationKey}
                                                          object={selectedBagelObject.key}
                                                          originObject={selectedObject.originObject}
                                                          onSave={async (updatedField) => {
                                                            await updateFieldAsync({
                                                              fieldName:
                                                                field.key || field.fieldPath,
                                                              field: updatedField,
                                                            });
                                                          }}
                                                          onCancel={() => {
                                                            // No-op: template field settings box handles its own cancel
                                                          }}
                                                          onDelete={
                                                            handleDeleteField
                                                              ? () =>
                                                                  handleDeleteField(
                                                                    field.key || field.fieldPath,
                                                                  )
                                                              : undefined
                                                          }
                                                          isDeleting={isDeleting}
                                                        />
                                                      ) : (
                                                        <FieldSettingsBox
                                                          field={field}
                                                          updateField={updateFieldAsync}
                                                          integrationKey={integrationKey}
                                                          blacklistedFields={blacklistedFields}
                                                          onToggleBlacklist={handleToggleBlacklist}
                                                          isBlacklisting={isBlacklisting}
                                                          onStaticToggle={handleStaticToggle}
                                                          onDeleteField={handleDeleteField}
                                                          isDeleting={isDeleting}
                                                        />
                                                      )}
                                                    </Box>
                                                  </Collapse>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      },
                                    )}
                                </tbody>
                              </Table>
                            </Collapse>
                          </td>
                        </tr>
                      </>
                    )}
                  {/* Custom Mapped Fields (editable select) */}
                  {customMappedFields?.map((field: any, index: number) => {
                    const fieldIdentifier =
                      field.type === 'static'
                        ? `static-${field.key || field.fieldKey || field.name}-${index}`
                        : field.originName || field.key || field.fieldKey;
                    const rowKey = `custom-${fieldIdentifier}-${index}`;
                    const externalField = selectedObject.fieldsMeta?.find(
                      (f) => f.key === field.originName,
                    );
                    const isBlacklisted = blacklistedFields.includes(field.originName || field.key);
                    // Find the latest field from object2Fields by fieldKey or originName
                    // For static fields, prioritize fieldKey matching
                    const latestField =
                      object2Fields?.find((f: any) => {
                        if (field.type === 'static') {
                          // For static fields, match by fieldKey first, then key
                          return f.fieldKey === field.fieldKey || f.key === field.key;
                        } else {
                          // For regular fields, use original logic
                          return (
                            f.fieldKey === field.fieldKey ||
                            f.originName === field.originName ||
                            f.key === field.key
                          );
                        }
                      }) || field;

                    // Debug logging for static fields
                    if (field.type === 'static') {
                      console.log('Static field debug:', {
                        fieldIdentifier,
                        fieldKey: field.fieldKey,
                        fieldName: field.key || field.name,
                        originalValue: field.value,
                        latestFieldValue: latestField.value,
                        staticFieldValue: staticFieldValues[fieldIdentifier],
                      });
                    }

                    // Determine if we need to show the extra select(s)
                    const mappingTypes = [
                      {
                        key: 'idea',
                        enabled: !!(
                          selectedObject?.extraParams?.createLinkedIdeas ||
                          selectedObject?.extraParams?.updateLinkedIdeas
                        ),
                        label: 'Idea',
                        tooltip: 'Pick an Idea field to map to on creation',
                        options: [
                          { value: 'summary', label: 'Title' },
                          { value: 'description', label: 'Description' },
                          { value: 'description1', label: 'Description1' },
                          { value: 'description2', label: 'Description2' },
                          { value: 'description3', label: 'Description3' },
                          { value: 'description4', label: 'Description4' },
                          { value: 'description5', label: 'Description5' },
                          { value: 'description6', label: 'Description6' },
                          { value: 'status', label: 'Status' },
                          { value: 'priority', label: 'Priority' },
                          { value: 'labels', label: 'Origin Tags' },
                          { value: 'originType', label: 'Origin Type' },
                          { value: 'componentText', label: 'Component Text' },
                          { value: 'components', label: 'Components' },
                          { value: 'itemStatus', label: 'Item Status' },
                          { value: 'createdAt', label: 'Created At' },
                          { value: 'updatedAt', label: 'Updated At' },
                        ],
                        fieldKey: 'ideaFieldMapping',
                      },
                      {
                        key: 'evidence',
                        enabled: !!selectedObject?.extraParams?.createLinkedEvidence,
                        label: 'Evidence',
                        tooltip: 'Pick an Evidence field to map to on creation',
                        options: [
                          { value: 'title', label: 'Title' },
                          { value: 'text', label: 'Text' },
                          { value: 'originOrgId', label: 'Origin Org ID' },
                          { value: 'oneLineSummary', label: 'One Line Summary' },
                          { value: 'priority', label: 'Priority' },
                          { value: 'type', label: 'Type' },
                          { value: 'componentString', label: 'Component String' },
                          { value: 'createdAt', label: 'Created At' },
                          { value: 'updatedAt', label: 'Updated At' },
                        ],
                        fieldKey: 'evidenceFieldMapping',
                      },
                    ];
                    // Get current value for selects from field (if exists)
                    const ideaFieldMapping = field.ideaFieldMapping || '';
                    const evidenceFieldMapping = field.evidenceFieldMapping || '';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr style={{ height: '48px' }}>
                          <td style={{}}>
                            <Group spacing={2}>
                              {isBlacklisted && (
                                <Tooltip label="This field is blacklisted" withArrow>
                                  <span
                                    style={{
                                      marginRight: 8,
                                      verticalAlign: 'middle',
                                      color: 'red',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <IconBan size={18} />
                                  </span>
                                </Tooltip>
                              )}
                              <Stack spacing={2}>
                                <Group spacing={4} align="center">
                                  {latestField.type === 'static' && (
                                    <Tooltip label="Static field with fixed value" withArrow>
                                      <Text
                                        size="xs"
                                        mr={8}
                                        color="blue"
                                        weight={600}
                                        style={{
                                          backgroundColor: '#e3f2fd',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          border: '1px solid #90caf9',
                                        }}
                                      >
                                        STATIC
                                      </Text>
                                    </Tooltip>
                                  )}
                                  {(latestField.fieldType === 'template' ||
                                    field.fieldType === 'template') && (
                                    <Tooltip label="Template field with dynamic text" withArrow>
                                      <Text
                                        size="xs"
                                        mr={8}
                                        color="teal"
                                        weight={600}
                                        style={{
                                          backgroundColor: '#e0f2f1',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          border: '1px solid #4db6ac',
                                        }}
                                      >
                                        TEMPLATE
                                      </Text>
                                    </Tooltip>
                                  )}
                                  {(latestField.fieldType === 'processed' ||
                                    field.fieldType === 'processed') &&
                                    !(
                                      latestField.fieldType === 'template' ||
                                      field.fieldType === 'template'
                                    ) && (
                                      <Tooltip
                                        label="Processed field with pipeline transformation"
                                        withArrow
                                      >
                                        <Text
                                          size="xs"
                                          mr={8}
                                          color="purple"
                                          weight={600}
                                          style={{
                                            backgroundColor: '#f3e5f5',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            border: '1px solid #ce93d8',
                                          }}
                                        >
                                          PROCESSED
                                        </Text>
                                      </Tooltip>
                                    )}
                                  <Box>
                                    <Text size="sm" weight={500}>
                                      {latestField.type === 'static'
                                        ? field.key || field.fieldKey || 'Static Field'
                                        : latestField.fieldType === 'template' ||
                                            field.fieldType === 'template'
                                          ? field.name ||
                                            field.key ||
                                            field.fieldKey ||
                                            'Template Field'
                                          : latestField.fieldType === 'processed' ||
                                              field.fieldType === 'processed'
                                            ? field.name ||
                                              field.key ||
                                              field.fieldKey ||
                                              'Processed Field'
                                            : externalField?.label || field.originName}
                                    </Text>

                                    <Text size="xs" color="dimmed">
                                      {(latestField.type === 'static'
                                        ? field.key || field.fieldKey || field.name
                                        : latestField.fieldType === 'template' ||
                                            field.fieldType === 'template'
                                          ? field.key || field.fieldKey || field.name
                                          : latestField.fieldType === 'processed' ||
                                              field.fieldType === 'processed'
                                            ? field.key || field.fieldKey || field.name
                                            : externalField?.key || field.originName || field.key) +
                                        ' '}
                                      (
                                      {latestField.type === 'static'
                                        ? 'static'
                                        : latestField.fieldType === 'template' ||
                                            field.fieldType === 'template'
                                          ? 'template'
                                          : latestField.fieldType === 'processed' ||
                                              field.fieldType === 'processed'
                                            ? 'processed'
                                            : externalField?.type || field.type || '-'}
                                      )
                                    </Text>
                                  </Box>
                                </Group>
                              </Stack>
                            </Group>
                          </td>
                          <td style={{ backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                            <Stack spacing={4}>
                              {latestField.type === 'static' ? (
                                <Group position="apart" spacing={0} noWrap>
                                  <TextInput
                                    w="100%"
                                    styles={{
                                      input: {
                                        borderRadius: '4px 0 0 4px',
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #90caf9',
                                        borderRight: 'none',
                                      },
                                    }}
                                    value={
                                      staticFieldValues[fieldIdentifier] !== undefined
                                        ? staticFieldValues[fieldIdentifier]
                                        : latestField.value || ''
                                    }
                                    onChange={(event) => {
                                      const newValue = event.currentTarget.value;
                                      setStaticFieldValues((prev) => ({
                                        ...prev,
                                        [fieldIdentifier]: newValue,
                                      }));
                                    }}
                                    placeholder="Enter static value"
                                    disabled={fieldUpdating === fieldIdentifier}
                                  />
                                  <ActionIcon
                                    sx={{
                                      borderRadius: '0 4px 4px 0',
                                    }}
                                    color="blue"
                                    variant="filled"
                                    onClick={async () => {
                                      const currentValue =
                                        staticFieldValues[fieldIdentifier] !== undefined
                                          ? staticFieldValues[fieldIdentifier]
                                          : latestField.value || '';
                                      await handleStaticToggle(latestField, true, currentValue);
                                      // Clear the local state after saving
                                      setStaticFieldValues((prev) => {
                                        const newState = { ...prev };
                                        delete newState[fieldIdentifier];
                                        return newState;
                                      });
                                    }}
                                    disabled={fieldUpdating === fieldIdentifier}
                                    w={36}
                                    h={36}
                                  >
                                    <IconCheck size={16} />
                                  </ActionIcon>
                                </Group>
                              ) : latestField.fieldType === 'template' ||
                                field.fieldType === 'template' ? (
                                <Box>
                                  <Group spacing={4} align="center">
                                    <Text size="sm" weight={500} style={{ color: '#00695c' }}>
                                      Template Configured
                                    </Text>
                                  </Group>
                                  <Text size="xs" color="dimmed" mt={4}>
                                    Click the 3-dot menu to edit the template
                                  </Text>
                                </Box>
                              ) : latestField.fieldType === 'processed' ||
                                field.fieldType === 'processed' ? (
                                <Box>
                                  <Group spacing={4} align="center">
                                    <IconCalculator size={16} style={{ color: '#9c27b0' }} />
                                    <Text size="sm" weight={500} style={{ color: '#9c27b0' }}>
                                      Pipeline Configured
                                    </Text>
                                  </Group>
                                  <Text size="xs" color="dimmed" mt={4}>
                                    Click the 3-dot menu to edit the pipeline
                                  </Text>
                                </Box>
                              ) : (
                                <Group spacing={4} noWrap sx={{ width: '100%' }}>
                                  {field.key && isSavedFieldKey(field.key) && (
                                    <IconKey
                                      size={14}
                                      style={{ color: '#5c5ceb', flexShrink: 0 }}
                                    />
                                  )}
                                  <Select
                                    sx={{ flex: 1, minWidth: 0 }}
                                    styles={{ dropdown: { minWidth: 220 } }}
                                    data={(() => {
                                      const items = (
                                        getFieldsWithDisabledState(field.originName || '') || []
                                      )
                                        .filter((f: any) => f && f.value && f.label)
                                        .map((f: any) => ({
                                          ...f,
                                          value: String(f.value || ''),
                                          label: String(f.label || f.value || ''),
                                        }));
                                      if (
                                        field.key &&
                                        !items.some((i: any) => i.value === field.key)
                                      ) {
                                        items.unshift({ value: field.key, label: field.key });
                                      }
                                      return items;
                                    })()}
                                    value={field.key}
                                    onChange={(value) =>
                                      handleFieldMappingChange(field.originName || '', value || '')
                                    }
                                    placeholder="Select field"
                                    clearable
                                    searchable
                                    withinPortal
                                    creatable
                                    itemComponent={({ query, ...props }) => (
                                      <BagelFieldSelectItem
                                        {...props}
                                        query={customMappingSearches[field.originName || ''] || ''}
                                      />
                                    )}
                                    getCreateLabel={(query) => `+ Create \"${query}\"`}
                                    onCreate={(query) => {
                                      const newField = {
                                        value: String(query || ''),
                                        label: String(query || ''),
                                      };
                                      setAvailableBagelFields((prev) => {
                                        if (prev.some((field) => field.value === String(query))) {
                                          return prev;
                                        }
                                        return [...prev, newField];
                                      });
                                      return String(query);
                                    }}
                                    filter={getCustomMappingFilter(field.originName || '')}
                                    searchValue={
                                      customMappingSearches[field.originName || ''] || ''
                                    }
                                    onSearchChange={(query) => {
                                      setCustomMappingSearches((prev) => ({
                                        ...prev,
                                        [field.originName || '']: query,
                                      }));
                                    }}
                                  />
                                </Group>
                              )}
                              {/* Extra select(s) for idea/evidence mapping, rendered separately - only for non-static, non-processed, and non-template fields */}
                              {latestField.type !== 'static' &&
                                latestField.fieldType !== 'processed' &&
                                latestField.fieldType !== 'template' &&
                                (selectedObject?.extraParams?.createLinkedIdeas ||
                                  selectedObject?.extraParams?.updateLinkedIdeas) && (
                                  <Group spacing={6} align="center">
                                    <Tooltip
                                      label="Pick an Idea field to map to on creation"
                                      withArrow
                                    >
                                      <span
                                        style={{ display: 'inline-flex', alignItems: 'center' }}
                                      >
                                        <IconCornerDownRightDouble
                                          size={22}
                                          style={{ marginRight: 4, color: '#5c5ceb' }}
                                        />
                                      </span>
                                    </Tooltip>
                                    <Select
                                      data={
                                        mappingTypes.find((mt) => mt.key === 'idea')?.options || []
                                      }
                                      value={latestField.ideaFieldMapping || ''}
                                      onChange={async (value) => {
                                        await handleFieldMappingExtraChange(
                                          field.originName,
                                          field.key,
                                          'ideaFieldMapping',
                                          value || '',
                                        );
                                      }}
                                      placeholder="Map to Idea field"
                                      withinPortal
                                      styles={{
                                        input: { minWidth: 140 },
                                        dropdown: { minWidth: 220 },
                                      }}
                                      creatable
                                      searchable
                                      getCreateLabel={(query) => `+ Create "${query}"`}
                                      onCreate={(query) => {
                                        const mt = mappingTypes.find((mt) => mt.key === 'idea');
                                        if (mt) mt.options.push({ value: query, label: query });
                                        return query;
                                      }}
                                    />
                                  </Group>
                                )}
                              {latestField.type !== 'static' &&
                                latestField.fieldType !== 'processed' &&
                                latestField.fieldType !== 'template' &&
                                selectedObject?.extraParams?.createLinkedEvidence && (
                                  <Group spacing={6} align="center">
                                    <Tooltip
                                      label="Pick an Evidence field to map to on creation"
                                      withArrow
                                    >
                                      <span
                                        style={{ display: 'inline-flex', alignItems: 'center' }}
                                      >
                                        <IconCornerDownRightDouble
                                          size={22}
                                          style={{ marginRight: 4, color: '#5c5ceb' }}
                                        />
                                      </span>
                                    </Tooltip>
                                    <Select
                                      data={
                                        mappingTypes.find((mt) => mt.key === 'evidence')?.options ||
                                        []
                                      }
                                      value={latestField.evidenceFieldMapping || ''}
                                      onChange={async (value) => {
                                        await handleFieldMappingExtraChange(
                                          field.originName,
                                          field.key,
                                          'evidenceFieldMapping',
                                          value || '',
                                        );
                                      }}
                                      placeholder="Map to Evidence field"
                                      withinPortal
                                      styles={{
                                        input: { minWidth: 140 },
                                        dropdown: { minWidth: 220 },
                                      }}
                                      creatable
                                      getCreateLabel={(query) => `+ Create "${query}"`}
                                      onCreate={(query) => {
                                        const mt = mappingTypes.find((mt) => mt.key === 'evidence');
                                        if (mt) mt.options.push({ value: query, label: query });
                                        return query;
                                      }}
                                    />
                                  </Group>
                                )}
                            </Stack>
                          </td>
                          <td style={{ textAlign: 'center', position: 'relative' }}>
                            <Tooltip withArrow label="Configure" withinPortal>
                              <ActionIcon
                                onClick={() =>
                                  setOpenSettingsRow(openSettingsRow === rowKey ? null : rowKey)
                                }
                              >
                                <IconDotsVertical size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </td>
                        </tr>
                        {/* Collapsible settings row */}
                        {openSettingsRow === rowKey && (
                          <tr>
                            <td colSpan={4} style={{ background: '#f8f9fa', padding: 0 }}>
                              <Collapse in={true}>
                                <Box
                                  p="sm"
                                  sx={{
                                    boxShadow:
                                      'inset 0 8px 12px -8px rgba(0,0,0,0.10), inset 0 -8px 12px -8px rgba(0,0,0,0.10)',
                                  }}
                                >
                                  {field.fieldType === 'processed' ? (
                                    <ProcessedFieldSettingsBox
                                      field={field}
                                      updateField={updateFieldAsync}
                                      integrationKey={integrationKey}
                                      object={selectedBagelObject.key}
                                      originObject={selectedObject.originObject || ''}
                                      onDeleteField={handleDeleteField}
                                      isDeleting={isDeleting}
                                    />
                                  ) : field.fieldType === 'template' ? (
                                    <TemplateFieldSettingsBox
                                      field={field}
                                      availableFields={[
                                        ...(availableExternalObject?.fields || []).map((f: any) => {
                                          // For original fields, use key, path, fieldPath, name, or label as fallback
                                          const fieldKey =
                                            f.key || f.path || f.fieldPath || f.name || f.label;
                                          return {
                                            key: fieldKey,
                                            label: f.label || f.name || fieldKey,
                                            group: 'Original' as const,
                                            type: f.type || 'text',
                                          };
                                        }),
                                        ...customMappedFields.map((f: any) => ({
                                          key: f.key || f.fieldPath || f.name,
                                          label: f.name || f.key || f.fieldPath,
                                          group: 'Mapped' as const,
                                          type: f.type || 'text',
                                        })),
                                      ]}
                                      integrationKey={integrationKey}
                                      object={selectedBagelObject.key}
                                      originObject={selectedObject.originObject}
                                      onSave={async (updatedField) => {
                                        await updateFieldAsync({
                                          fieldName: field.key || field.fieldPath,
                                          field: updatedField,
                                        });
                                      }}
                                      onCancel={() => {
                                        // No-op: template field settings box handles its own cancel
                                      }}
                                      onDelete={
                                        handleDeleteField
                                          ? () => handleDeleteField(field.key || field.fieldPath)
                                          : undefined
                                      }
                                      isDeleting={isDeleting}
                                    />
                                  ) : (
                                    <FieldSettingsBox
                                      field={field}
                                      updateField={updateFieldAsync}
                                      integrationKey={integrationKey}
                                      blacklistedFields={blacklistedFields}
                                      onToggleBlacklist={handleToggleBlacklist}
                                      isBlacklisting={isBlacklisting}
                                      onStaticToggle={handleStaticToggle}
                                      onDeleteField={handleDeleteField}
                                      isDeleting={isDeleting}
                                    />
                                  )}
                                </Box>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* New Mapping Row - shown outside the accordion */}
                  {showNewMappingRow &&
                    (() => {
                      // Ensure all bagel fields have proper string values
                      const safeBagelFields = availableBagelFields
                        .filter((f) => f && f.value && f.label)
                        .map((f) => ({
                          ...f,
                          value: String(f.value || ''),
                          label: String(f.label || f.value || ''),
                        }));

                      // Add all saved field keys to the options
                      const savedFieldKeysOptions = SAVED_FIELD_KEYS.map((savedKey) => ({
                        value: savedKey.key,
                        label: savedKey.key,
                        isSavedField: true,
                        savedFieldDescription: savedKey.description,
                      }));

                      // Merge saved keys with existing fields, avoiding duplicates
                      const allBagelFieldOptions = [
                        ...savedFieldKeysOptions.filter(
                          (saved) => !safeBagelFields.some((f) => f.value === saved.value),
                        ),
                        ...safeBagelFields,
                      ];

                      let bagelFieldOptions = allBagelFieldOptions;
                      if (
                        newMapping?.bagelField &&
                        !bagelFieldOptions.some((f) => f.value === newMapping.bagelField)
                      ) {
                        bagelFieldOptions = [
                          {
                            value: String(newMapping.bagelField),
                            label: String(newMapping.bagelField),
                          },
                          ...bagelFieldOptions,
                        ];
                      }
                      return (
                        <tr style={{ height: '48px' }}>
                          <td style={{ width: '40%' }}>
                            <Select
                              data={availableExternalFields
                                .filter((f) => f && (f.key || f.value) && f.label)
                                .map((f) => ({
                                  value: String(f.key || f.value || ''),
                                  label: String(f.label || f.key || f.value || ''),
                                  fillPercentage: f.fillPercentage,
                                  hasStatistics: f.hasStatistics,
                                  totalRecords: f.totalRecords,
                                  nonNullCount: f.nonNullCount,
                                  types: f.types,
                                  required: f.required,
                                  length: f.length,
                                }))}
                              value={newMapping?.externalField}
                              onChange={(value) => {
                                setNewMapping(
                                  value
                                    ? {
                                        externalField: value,
                                        bagelField: newMapping?.bagelField || '',
                                      }
                                    : null,
                                );
                              }}
                              placeholder="Select external field"
                              searchable
                              clearable
                              withinPortal
                              creatable
                              itemComponent={({ query, ...props }) => (
                                <ExternalFieldSelectItem {...props} query={externalFieldSearch} />
                              )}
                              getCreateLabel={(query) => `+ Create \"${query}\"`}
                              onCreate={(query) => {
                                const newField = {
                                  key: String(query || ''),
                                  label: String(query || ''),
                                  value: String(query || ''),
                                };
                                setAvailableExternalFields((prev) => {
                                  if (prev.some((field) => field.key === String(query))) {
                                    return prev;
                                  }
                                  return [...prev, newField];
                                });
                                setNewMapping({
                                  externalField: String(query),
                                  bagelField: newMapping?.bagelField || '',
                                });
                                return String(query);
                              }}
                              filter={externalFieldFilter}
                              searchValue={externalFieldSearch}
                              onSearchChange={setExternalFieldSearch}
                            />
                          </td>
                          <td
                            style={{
                              width: '48%',
                              backgroundColor: 'rgba(92, 92, 235, 0.05)',
                              overflow: 'hidden',
                              maxWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                width: '100%',
                                minWidth: 0,
                                alignItems: 'stretch',
                              }}
                            >
                              <ActionIcon
                                color="gray"
                                variant="outline"
                                onClick={() => {
                                  setShowNewMappingRow(false);
                                  setNewMapping(null);
                                  setNewMappingType('imported');
                                }}
                                w={36}
                                h={36}
                                sx={{
                                  borderRadius: '4px 0 0 4px',
                                  borderColor: '#cfd4da',
                                  borderRight: 'none',
                                  flexShrink: 0,
                                }}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                              <Select
                                value={newMappingType}
                                onChange={(
                                  value: 'imported' | 'static' | 'processed' | 'template',
                                ) => {
                                  setNewMappingType(value);
                                  // Reset mapping when changing types
                                  setNewMapping((prev) =>
                                    prev
                                      ? {
                                          externalField: prev.externalField,
                                          bagelField: '',
                                          staticValue: undefined,
                                        }
                                      : null,
                                  );
                                }}
                                data={[
                                  { value: 'imported', label: 'Imported' },
                                  { value: 'static', label: 'Static' },
                                  { value: 'processed', label: 'Processed' },
                                  { value: 'template', label: 'Template' },
                                ]}
                                styles={{
                                  root: { width: 108, flexShrink: 0 },
                                  input: {
                                    borderRadius: 0,
                                    borderRight: 'none',
                                    height: 36,
                                    fontSize: '12px',
                                  },
                                }}
                                size="xs"
                              />
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'stretch',
                                }}
                              >
                                {newMappingType === 'static' ? (
                                  <TextInput
                                    styles={{
                                      root: { flex: 1, minWidth: 0, width: '100%' },
                                      wrapper: { flex: 1, minWidth: 0 },
                                      input: {
                                        borderRadius: 0,
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #90caf9',
                                        borderRight: 'none',
                                        height: 36,
                                      },
                                    }}
                                    value={newMapping?.staticValue || ''}
                                    onChange={(event) =>
                                      setNewMapping((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              staticValue: event.currentTarget.value,
                                            }
                                          : {
                                              externalField: '',
                                              bagelField: '',
                                              staticValue: event.currentTarget.value,
                                            },
                                      )
                                    }
                                    placeholder="Enter static value"
                                  />
                                ) : newMappingType === 'processed' ? (
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: 'hidden',
                                      borderRadius: 0,
                                      backgroundColor: '#f3e5f5',
                                      border: '1px solid #ce93d8',
                                      borderRight: 'none',
                                      padding: '0 10px',
                                      height: 36,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                  >
                                    <IconCalculator
                                      size={16}
                                      style={{ color: '#9c27b0', flexShrink: 0 }}
                                    />
                                    <Text
                                      size="xs"
                                      color="#9c27b0"
                                      truncate
                                      title="Configure after creation"
                                    >
                                      Configure after creation
                                    </Text>
                                  </Box>
                                ) : newMappingType === 'template' ? (
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: 'hidden',
                                      borderRadius: 0,
                                      backgroundColor: '#e0f2f1',
                                      border: '1px solid #4db6ac',
                                      borderRight: 'none',
                                      padding: '0 10px',
                                      height: 36,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Tooltip label="Configure template after creation" withArrow>
                                      <Text
                                        size="xs"
                                        color="#00695c"
                                        truncate
                                        sx={{ minWidth: 0, flex: 1 }}
                                      >
                                        Configure template after creation
                                      </Text>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  <Group
                                    spacing={4}
                                    noWrap
                                    sx={{ flex: 1, minWidth: 0, width: '100%' }}
                                  >
                                    {newMapping?.bagelField &&
                                      isSavedFieldKey(newMapping.bagelField) && (
                                        <IconKey
                                          size={14}
                                          style={{ color: '#5c5ceb', flexShrink: 0 }}
                                        />
                                      )}
                                    <Select
                                      styles={{
                                        root: { flex: 1, minWidth: 0 },
                                        input: {
                                          borderRadius: 0,
                                          borderRight: 'none',
                                          height: 36,
                                        },
                                        dropdown: { minWidth: 220 },
                                      }}
                                      data={bagelFieldOptions}
                                      value={newMapping?.bagelField}
                                      onChange={(value) =>
                                        setNewMapping(
                                          value
                                            ? {
                                                bagelField: value,
                                                externalField: newMapping?.externalField || '',
                                              }
                                            : null,
                                        )
                                      }
                                      placeholder="Select Bagel field"
                                      searchable
                                      clearable
                                      withinPortal
                                      creatable
                                      itemComponent={({ query, ...props }) => (
                                        <BagelFieldSelectItem {...props} query={bagelFieldSearch} />
                                      )}
                                      getCreateLabel={(query) => `+ Create \"${query}\"`}
                                      onCreate={(query) => {
                                        const newField = {
                                          value: String(query || ''),
                                          label: String(query || ''),
                                        };
                                        setAvailableBagelFields((prev) => {
                                          if (prev.some((field) => field.value === String(query))) {
                                            return prev;
                                          }
                                          return [...prev, newField];
                                        });
                                        return String(query);
                                      }}
                                      filter={bagelFieldFilter}
                                      searchValue={bagelFieldSearch}
                                      onSearchChange={setBagelFieldSearch}
                                    />
                                  </Group>
                                )}
                              </Box>
                              <ActionIcon
                                sx={{
                                  borderRadius: '0 4px 4px 0',
                                  flexShrink: 0,
                                  backgroundColor:
                                    newMappingType === 'processed'
                                      ? '#ce93d8'
                                      : newMappingType === 'static'
                                        ? '#90caf9'
                                        : newMappingType === 'template'
                                          ? '#4db6ac'
                                          : '#4d6ef5',
                                }}
                                variant="filled"
                                color="indigo"
                                onClick={handleAddNewMapping}
                                disabled={
                                  !newMapping?.externalField ||
                                  (newMappingType === 'imported' && !newMapping?.bagelField) ||
                                  (newMappingType === 'static' && !newMapping?.staticValue)
                                }
                                w={36}
                                h={36}
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                            </Box>
                          </td>
                          <td style={{ width: '8%' }}></td>
                        </tr>
                      );
                    })()}
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td colSpan={4}>
                      <Button
                        variant="outline"
                        color="indigo"
                        fullWidth
                        size="xs"
                        onClick={() => setShowNewMappingRow(true)}
                        disabled={showNewMappingRow}
                        leftIcon={<IconPlus size={16} />}
                      >
                        Add Field Mapping
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Box>
          </ScrollArea>
        </Box>
      </Box>
    </Drawer>
  );
};
